import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { FiPlusCircle, FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { CertificateInput } from "../../inputs/certificate-input";
import { CheckboxInput } from "../../../inputs/checkbox-input";
import { deleteTutorDocument, updateTutorDocuments } from "../../../../store/profile/profileActions";
import { selectTutorProfile, TutorDocumentsType } from "../../../../store/profile/profileSlice";

const DELETE_TYPE = {
    [TutorDocumentsType.education]: "education",
    [TutorDocumentsType.experience]: "experiences",
    [TutorDocumentsType.certificate]: "certificates",
};

const PROFILE_LIST_KEY = {
    [TutorDocumentsType.education]: "education",
    [TutorDocumentsType.experience]: "experience",
    [TutorDocumentsType.certificate]: "certificate",
};

const emptyEntry = (type) => {
    if (type === TutorDocumentsType.certificate) {
        return { _id: "", label: "", year: "", file: "", description: "" };
    }
    return { _id: "", label: "", from: "", to: "", file: "", description: "", school: "" };
};

const fieldPathsForIndex = (name, index, takeTime) => {
    if (!takeTime) {
        return [
            `${name}.${index}.label`,
            `${name}.${index}.year`,
        ];
    }
    return [
        `${name}.${index}.label`,
        `${name}.${index}.school`,
        `${name}.${index}.from`,
        `${name}.${index}.to`,
    ];
};

const SavedDocumentCard = ({ element, onRemove, takeTime }) => {
    const [confirmRemove, setConfirmRemove] = useState(false);
    const docId = element._id || element.id;

    return (
        <div className="border border-nt-grey-light flex flex-col justify-between mb-2 rounded-xl p-3 bg-nt-grey-light/30">
            <div className="flex justify-between items-start gap-2">
                <h6 className="text-nt-blue font-medium">{element.label}</h6>
                {confirmRemove ? (
                    <div className="flex items-center shrink-0 text-sm">
                        <p className="mr-1">Remove it?</p>
                        <button
                            type="button"
                            className="mr-1 text-nt-red font-medium"
                            onClick={(e) => {
                                e.preventDefault();
                                onRemove(docId);
                                setConfirmRemove(false);
                            }}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            className="mr-1"
                            onClick={(e) => {
                                e.preventDefault();
                                setConfirmRemove(false);
                            }}
                        >
                            No
                        </button>
                    </div>
                ) : (
                    <button type="button" onClick={() => setConfirmRemove(true)} aria-label="Remove document">
                        <FiTrash2 size={20} color="#df2b47" />
                    </button>
                )}
            </div>
            <span className="min-w-[150px] text-nt-blue text-sm">
                {takeTime
                    ? `${element.from} - ${element.toPresent ? "Present" : element.to}`
                    : element.year}
            </span>
            {takeTime && (element.school || element.company) ? (
                <span className="text-nt-grey text-sm">{element.school || element.company}</span>
            ) : null}
            {element.description ? (
                <p className="text-[14px] mt-1">{element.description}</p>
            ) : null}
        </div>
    );
};

const DocumentsSection = forwardRef(function DocumentsSection(
    {
        type,
        name,
        noDocumentField,
        checkboxLabel,
        title,
        locationLabel = "School",
        takeTime = true,
    },
    ref,
) {
    const dispatch = useDispatch();
    const tutorProfile = useSelector(selectTutorProfile);
    const [savingIndex, setSavingIndex] = useState(null);
    const [sectionError, setSectionError] = useState("");

    const {
        register,
        watch,
        getValues,
        setValue,
        control,
        trigger,
        formState: { errors },
    } = useFormContext();

    const {
        fields,
        append,
        remove,
    } = useFieldArray({
        control,
        name,
    });

    const noDocument = watch(noDocumentField);
    const listKey = PROFILE_LIST_KEY[type];
    const savedDocs = Array.isArray(tutorProfile?.[listKey]) ? tutorProfile[listKey] : [];

    const addEmpty = () => {
        append(emptyEntry(type));
    };

    useEffect(() => {
        if (!noDocument && fields.length === 0 && savedDocs.length === 0) {
            addEmpty();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noDocument, fields.length, savedDocs.length]);

    const removeSaved = async (id) => {
        await dispatch(deleteTutorDocument({ id, name: DELETE_TYPE[type] })).unwrap();
    };

    const saveItemAt = async (index) => {
        const paths = fieldPathsForIndex(name, index, takeTime);
        const valid = await trigger(paths);
        if (!valid) return false;

        setSavingIndex(index);
        setSectionError("");
        try {
            const item = getValues(`${name}.${index}`);
            await dispatch(updateTutorDocuments({
                documents: [item],
                noDocument: false,
                type,
            })).unwrap();
            remove(index);
            return true;
        } catch {
            return false;
        } finally {
            setSavingIndex(null);
        }
    };

    const flushAndValidate = async () => {
        setSectionError("");

        if (noDocument) {
            if (savedDocs.length > 0) {
                // User checked "no X" but still has saved docs — clear via API
                await dispatch(updateTutorDocuments({
                    documents: [],
                    noDocument: true,
                    type,
                })).unwrap();
            } else if (!tutorProfile?.[noDocumentField]) {
                await dispatch(updateTutorDocuments({
                    documents: [],
                    noDocument: true,
                    type,
                })).unwrap();
            }
            // Clear any leftover open forms
            if (fields.length) {
                remove();
            }
            return true;
        }

        // Save every open form entry (high → low so remove() indices stay valid)
        const openCount = getValues(name)?.length ?? 0;
        let savedThisRound = 0;
        for (let i = openCount - 1; i >= 0; i -= 1) {
            const ok = await saveItemAt(i);
            if (!ok) {
                setSectionError("Please complete or remove the open entries before continuing.");
                return false;
            }
            savedThisRound += 1;
        }

        if (savedThisRound === 0 && savedDocs.length === 0) {
            setSectionError(`Add at least one ${title.toLowerCase()} entry, or check "${checkboxLabel}".`);
            return false;
        }

        return true;
    };

    useImperativeHandle(ref, () => ({
        flushAndValidate,
    }));

    return (
        <section className="mb-8 rounded-xl border border-nt-grey-light p-4 md:p-5">
            <h3 className="text-nt-blue text-lg font-semibold mb-3">{title}</h3>

            <CheckboxInput
                name={noDocumentField}
                label={checkboxLabel}
                register={register}
                control={control}
                style={{ marginBottom: "16px" }}
            />

            {sectionError ? (
                <p className="text-nt-red text-sm mb-3">{sectionError}</p>
            ) : null}

            {!noDocument && (
                <>
                    <div className="mb-3">
                        {savedDocs.map((el, index) => (
                            <SavedDocumentCard
                                key={el._id || el.id || index}
                                element={el}
                                onRemove={removeSaved}
                                takeTime={takeTime}
                            />
                        ))}
                    </div>

                    {fields.map((field, index) => (
                        <div key={field.id} className="relative">
                            <CertificateInput
                                stored={savedDocs.length}
                                control={control}
                                register={register}
                                getValues={getValues}
                                watch={watch}
                                setValue={setValue}
                                id={field.id}
                                remove={remove}
                                name={name}
                                index={index}
                                errors={errors}
                                takeTime={takeTime}
                                locationLabel={locationLabel}
                            />
                            <button
                                type="button"
                                disabled={savingIndex === index}
                                onClick={() => saveItemAt(index)}
                                className={`mb-4 w-full rounded-lg py-2 text-sm font-medium text-white transition ${
                                    savingIndex === index
                                        ? "bg-nt-blue/50 cursor-not-allowed"
                                        : "bg-nt-blue hover:opacity-90"
                                }`}
                            >
                                {savingIndex === index ? "Saving…" : "Save"}
                            </button>
                        </div>
                    ))}

                    <button
                        type="button"
                        className="mb-2 flex justify-center m-auto w-full text-center"
                        onClick={addEmpty}
                        aria-label={`Add ${title}`}
                    >
                        <FiPlusCircle size={30} color="#191d51" fontWeight={700} />
                    </button>
                </>
            )}
        </section>
    );
});

export { DocumentsSection };
